
import { useRef } from 'react';
import { Camera, ImageOff, Info, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PAINEL_ROLE_LABELS } from '@/lib/painel-rbac';
import type { PainelRole } from '@/lib/painel-rbac';
import { AVATAR_ACCEPT } from '@/lib/painel-account-settings';
import {
  painelButtonOutlineClass,
  painelButtonSecondaryClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { AccountSettingsCard } from './account-settings-shared';

type AvatarUploaderProps = {
  displayName: string;
  role: PainelRole;
  email: string;
  avatarPreview: string | null;
  onAvatarChange: (preview: string | null, file?: File | null) => void;
  delay?: number;
};

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function AvatarUploader({
  displayName,
  role,
  email,
  avatarPreview,
  onAvatarChange,
  delay = 0,
}: AvatarUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | null) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) return;
    onAvatarChange(URL.createObjectURL(file), file);
  };

  const handleRemove = () => {
    if (avatarPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview);
    }
    onAvatarChange(null, null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <AccountSettingsCard
      title="Perfil"
      description="Sua identidade no painel administrativo — visível para outros membros da equipe."
      icon={UserCircle}
      accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/25"
      delay={delay}
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex shrink-0 flex-col items-center gap-4 sm:flex-row sm:items-start lg:flex-col lg:items-center">
          <div className="relative">
            <div
              className={cn(
                'flex h-[7.5rem] w-[7.5rem] items-center justify-center overflow-hidden rounded-2xl',
                'bg-gradient-to-br from-[#0e1f7a] to-[#091460] ring-2 ring-white/10',
                'shadow-[0_8px_32px_-8px_rgba(56,189,248,0.35)]'
              )}
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt={`Avatar de ${displayName}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="font-poppins text-3xl font-bold text-[#8aabf8]">
                  {getInitials(displayName)}
                </span>
              )}
            </div>
            <span className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-xl border border-sky-400/30 bg-blue-950/90 text-sky-300 shadow-lg">
              <Camera className="h-4 w-4" aria-hidden />
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:justify-start lg:justify-center">
            <Button
              type="button"
              size="sm"
              className={cn(painelButtonSecondaryClass, 'h-9 px-4')}
              onClick={() => inputRef.current?.click()}
            >
              Alterar avatar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={cn(painelButtonOutlineClass, 'h-9 px-4')}
              onClick={handleRemove}
              disabled={!avatarPreview}
            >
              <ImageOff className="mr-1.5 h-3.5 w-3.5" />
              Remover avatar
            </Button>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={AVATAR_ACCEPT}
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="min-w-0 flex-1 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="font-inter text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Nome
              </p>
              <p className="mt-1 font-poppins text-base font-semibold text-white">{displayName}</p>
            </div>
            <div>
              <p className="font-inter text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Cargo
              </p>
              <p className="mt-1 font-poppins text-base font-semibold text-sky-200">
                {PAINEL_ROLE_LABELS[role]}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="font-inter text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                E-mail
              </p>
              <p className="mt-1 font-inter text-sm text-slate-300">{email}</p>
            </div>
          </div>

          <div
            className={cn(
              'flex gap-3 rounded-xl border border-amber-400/20 bg-amber-500/[0.08] p-4',
              'font-inter text-xs leading-relaxed text-amber-100/90'
            )}
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-300/80" aria-hidden />
            <p>
              Formatos aceitos: JPG, PNG e WEBP (até 4 MB). O avatar aparece no header do painel
              e nos artigos dos guias que publicar ou editar.
            </p>
          </div>
        </div>
      </div>
    </AccountSettingsCard>
  );
}

/** Alias semântico para o card de perfil. */
export const ProfileCard = AvatarUploader;
